import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";

/**
 * Tailwind safelist for z-index classes used in nested dialogs:
 * z-[999] z-[1000] z-[1099] z-[1100] z-[1199] z-[1200]
 * z-[1299] z-[1300] z-[1399] z-[1400] z-[1499] z-[1500]
 * z-[1599] z-[1600]
 */
import { XIcon } from "lucide-react";

import { cn } from "@/lib/utils";
import { GridPattern } from "./grid-pattern";

function Dialog({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Root>) {
  return <DialogPrimitive.Root data-slot="dialog" {...props} />;
}

function DialogTrigger({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Trigger>) {
  return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />;
}

function DialogPortal({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Portal>) {
  return <DialogPrimitive.Portal data-slot="dialog-portal" {...props} />;
}

function DialogClose({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Close>) {
  return <DialogPrimitive.Close data-slot="dialog-close" {...props} />;
}

const OVERLAY_Z_INDEX: Record<number, string> = {
  0: "z-[999]",
  1: "z-[1099]",
  2: "z-[1199]",
  3: "z-[1299]",
  4: "z-[1399]",
  5: "z-[1499]",
  6: "z-[1599]",
};

const CONTENT_Z_INDEX: Record<number, string> = {
  0: "z-[1000]",
  1: "z-[1100]",
  2: "z-[1200]",
  3: "z-[1300]",
  4: "z-[1400]",
  5: "z-[1500]",
  6: "z-[1600]",
};

function DialogOverlay({
  className,
  nestedLevel = 0,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Overlay> & {
  nestedLevel?: number;
}) {
  const zClass = OVERLAY_Z_INDEX[nestedLevel] ?? OVERLAY_Z_INDEX[0];
  return (
    <DialogPrimitive.Overlay
      data-slot="dialog-overlay"
      className={cn(
        "backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 bg-black/40 backdrop-grayscale",
        zClass,
        className,
      )}
      {...props}
    />
  );
}

function DialogContent({
  className,
  children,
  showCloseButton = true,
  nestedLevel = 0,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Content> & {
  showCloseButton?: boolean;
  nestedLevel?: number;
}) {
  const contentZClass = CONTENT_Z_INDEX[nestedLevel] ?? CONTENT_Z_INDEX[0];
  return (
    <DialogPortal data-slot="dialog-portal">
      <DialogOverlay nestedLevel={nestedLevel} />
      <DialogPrimitive.Content
        data-slot="dialog-content"
        className={cn(
          "bg-popover text-popover-foreground data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 fixed top-[50%] left-[50%] grid w-full max-w-[calc(100%-2rem)] translate-x-[-50%] translate-y-[-50%] gap-4 rounded-lg border border-border/50 p-6 shadow-lg duration-200 sm:max-w-lg",
          contentZClass,
          className,
        )}
        {...props}
      >
        {children}
        {showCloseButton && (
          <DialogPrimitive.Close
            data-slot="dialog-close"
            className="ring-offset-background focus:ring-ring data-[state=open]:bg-accent data-[state=open]:text-muted-foreground absolute top-4 right-4 rounded-xs opacity-70 transition-opacity hover:opacity-100 focus:ring-2 focus:ring-offset-2 focus:outline-hidden disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4"
          >
            <XIcon />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Content>
    </DialogPortal>
  );
}

function DialogHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="dialog-header"
      className={cn("flex flex-col gap-2 text-center sm:text-left ", className)}
      {...props}
    />
  );
}

function DialogFooter({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="dialog-footer"
      className={cn(
        "flex flex-col-reverse gap-2 sm:flex-row sm:justify-end",
        className,
      )}
      {...props}
    />
  );
}

function DialogTitle({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Title>) {
  return (
    <DialogPrimitive.Title
      data-slot="dialog-title"
      className={cn("text-lg leading-none font-semibold", className)}
      {...props}
    />
  );
}

function DialogDescription({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Description>) {
  return (
    <DialogPrimitive.Description
      data-slot="dialog-description"
      className={cn("text-muted-foreground text-sm", className)}
      {...props}
    />
  );
}

export {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
};
